import { useQuery } from "@tanstack/react-query";
import { Button, Container, Dropdown, Nav, Navbar, NavDropdown } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";

import { listTenants } from "@/api/platformAdmin";
import { switchTenant } from "@/auth/actAs";
import { useAuth } from "@/auth/useAuth";
import { useTheme } from "@/theme/useTheme";

import { BrandLogo } from "./BrandLogo";
import { NotificationBell } from "./NotificationBell";

/** Platform-admin tenant switcher: pick a tenant to act inside (support/setup).
 *  Selecting reloads with the X-Act-As-Tenant header set (see auth/actAs). */
function TenantSwitcher() {
  const auth = useAuth();
  const actingId =
    auth.status === "authenticated" ? (auth.user?.acting_as?.tenant_id ?? null) : null;
  const { data: tenants } = useQuery({
    queryKey: ["platform-tenant-switcher"],
    queryFn: listTenants,
    staleTime: 60_000,
  });
  const currentSlug = actingId
    ? (tenants?.find((t) => t.id === actingId)?.slug ?? "…")
    : "platform (home)";
  return (
    <NavDropdown title={`Viewing: ${currentSlug}`} id="tenant-switcher" align="end">
      <NavDropdown.Item onClick={() => switchTenant(null)} active={!actingId}>
        Platform (home)
      </NavDropdown.Item>
      <NavDropdown.Divider />
      <NavDropdown.Header>Act as tenant</NavDropdown.Header>
      {(tenants ?? [])
        .filter((t) => t.slug !== "platform")
        .map((t) => (
          <NavDropdown.Item
            key={t.id}
            active={t.id === actingId}
            onClick={() => switchTenant(t.id)}
          >
            {t.name} <span className="text-muted small">({t.slug})</span>
          </NavDropdown.Item>
        ))}
    </NavDropdown>
  );
}

export function AppNav() {
  const auth = useAuth();
  const theme = useTheme();
  const location = useLocation();

  if (auth.status !== "authenticated" || !auth.user) return null;

  return (
    <Navbar expand="md" bg="body-tertiary" className="border-bottom mb-4">
      <Container fluid="lg">
        <Navbar.Brand
          as={Link}
          to="/dashboard"
          // d-flex align-items-center is load-bearing: without it, the
          // inline-flex BrandLogo sits on .navbar-brand's text baseline
          // (~78% from the top of its line-height box) rather than the
          // box's midline, so the brand visually rides ~3px above the
          // adjacent <Nav.Link>s. Making the brand itself a flex container
          // lets align-items center the BrandLogo on the Brand's box mid.
          className="text-decoration-none d-flex align-items-center"
        >
          <BrandLogo size="md" />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="primary-nav" />
        <Navbar.Collapse id="primary-nav">
          <Nav className="me-auto">
            <Nav.Link as={Link} to="/dashboard" active={location.pathname === "/dashboard"}>
              Dashboard
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/connections"
              active={
                location.pathname.startsWith("/connections") &&
                !location.pathname.startsWith("/connections/schedules") &&
                !location.pathname.startsWith("/connections/groups")
              }
            >
              Connections
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/connections/groups"
              active={location.pathname.startsWith("/connections/groups")}
            >
              Groups
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/connections/schedules"
              active={location.pathname.startsWith("/connections/schedules")}
            >
              Schedules
            </Nav.Link>
            {auth.hasPermission("connections:read") && (
              <Nav.Link
                as={Link}
                to="/inventory"
                active={location.pathname.startsWith("/inventory")}
              >
                Inventory
              </Nav.Link>
            )}
            {auth.hasPermission("connections:read") && (
              <Nav.Link
                as={Link}
                to="/telemetry"
                active={location.pathname.startsWith("/telemetry")}
              >
                Telemetry
              </Nav.Link>
            )}
            {(auth.hasPermission("users:read") ||
              auth.hasPermission("sso:configure") ||
              auth.hasPermission("lumen_accounts:configure") ||
              auth.hasPermission("tenant:configure") ||
              auth.hasPermission("audit:read")) && (
              <NavDropdown title="Admin" id="admin-menu">
                {auth.hasPermission("users:read") && (
                  <NavDropdown.Item as={Link} to="/admin/users">
                    Users
                  </NavDropdown.Item>
                )}
                {auth.hasPermission("roles:read") && (
                  <NavDropdown.Item as={Link} to="/admin/roles">
                    Roles
                  </NavDropdown.Item>
                )}
                {auth.hasPermission("lumen_accounts:configure") && (
                  <NavDropdown.Item as={Link} to="/admin/lumen-accounts">
                    Lumen Accounts
                  </NavDropdown.Item>
                )}
                {auth.hasPermission("connections:modify") && (
                  <NavDropdown.Item as={Link} to="/admin/bandwidth-ladders">
                    Bandwidth Ladders
                  </NavDropdown.Item>
                )}
                {auth.hasPermission("audit:read") && (
                  <NavDropdown.Item as={Link} to="/admin/audit-log">
                    Audit Log
                  </NavDropdown.Item>
                )}
                {auth.hasPermission("audit:read") && (
                  <NavDropdown.Item as={Link} to="/admin/jobs">
                    Background Jobs
                  </NavDropdown.Item>
                )}
                {auth.hasPermission("audit:read") && (
                  <NavDropdown.Item as={Link} to="/admin/emails/preview">
                    Email Previews
                  </NavDropdown.Item>
                )}
                {auth.hasPermission("tenant:configure") && (
                  <NavDropdown.Item as={Link} to="/admin/settings">
                    Organization Settings
                  </NavDropdown.Item>
                )}
                {auth.hasPermission("tenant:configure") && (
                  <NavDropdown.Item as={Link} to="/admin/identity-providers">
                    Identity Providers
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            )}
            {auth.hasPermission("platform:admin") && (
              <>
                <NavDropdown title="Platform" id="platform-menu">
                  <NavDropdown.Item as={Link} to="/admin/platform/tenants">
                    Tenants
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/platform/settings">
                    Settings (Email / SMTP)
                  </NavDropdown.Item>
                </NavDropdown>
                <TenantSwitcher />
              </>
            )}
          </Nav>

          {/* align-items is responsive on purpose. Expanded (md+) this Nav is a
              flex ROW, so align-items-center centres the bell/theme/user
              controls vertically against the nav links. Collapsed, .navbar-nav
              becomes a COLUMN and the cross axis flips to horizontal — the same
              class would centre them left-to-right while the links above stay
              left, which is the misalignment this avoids. -start rather than the
              stretch default because these are content-sized controls that
              would otherwise go full-width. */}
          <Nav className="align-items-start align-items-md-center gap-2">
            <NotificationBell />
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={theme.toggle}
              aria-label="Toggle theme"
              title={theme.mode === "dark" ? "Switch to light" : "Switch to dark"}
            >
              {theme.mode === "dark" ? "☾" : "☼"}
            </Button>
            <Dropdown align="end">
              <Dropdown.Toggle variant="outline-secondary" size="sm" id="user-menu">
                {auth.user.name ?? auth.user.email}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.ItemText className="text-muted small">
                  {auth.user.email}
                </Dropdown.ItemText>
                <Dropdown.ItemText className="text-muted small">
                  Tenant: {auth.user.tenant_slug}
                </Dropdown.ItemText>
                <Dropdown.Divider />
                <Dropdown.Item as={Link} to="/profile">
                  Profile
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={auth.logout}>Sign out</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}
